import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { PopupNames } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';

import { Funnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import { usePopup } from 'hooks/usePopup';
import { useRefreshTimer } from 'hooks/useRefreshTimer';
import { useUpgradePageTransition } from 'hooks/useUpgradePageTransition';
import { getAuthenticated, getUser } from 'store/auth/selectors';
import { isLimitTimeOfferEnabledSelector } from 'store/common/selectors';

export const TIMER_NAME = 'LIMIT_TIME_OFFER';
export const TIMEOUT_DURATION = 300 * 1000; // 300 sec

export const useLimitTimeOfferPopup = () => {
  const isAuthenticated = useSelector(getAuthenticated);
  const isLimitedTimeOfferEnabled =
    useSelector(isLimitTimeOfferEnabledSelector) || true;

  const user = useSelector(getUser);

  const isUserBuyer = user && user.is_buyer;

  const {
    handleOpenASAP,
    handleClose,
    handleAction,
    checkIsOpenOrInQueue,
    handleRemoveFromQueue,
  } = usePopup(PopupNames.LimitTimeOffer);

  const { handleStartTimer, handleStopTimer, handleRemoveTimer } =
    useRefreshTimer({
      name: TIMER_NAME,
      ms: TIMEOUT_DURATION,
      cb: () => handleOpenASAP(null),
    });

  const { handleSafePushUpgradePage } = useUpgradePageTransition();

  handleAction.current = (action: { type: string }) => {
    if (action.type === 'submit') {
      const search = getFunnelSearchString(Funnels.LimitOffer);

      handleClose();
      handleSafePushUpgradePage(search);
    } else if (action.type === 'close') {
      handleClose();
    }
  };

  useEffect(() => {
    if (user && user.is_premium) {
      handleStopTimer();
      handleRemoveFromQueue();
      handleRemoveTimer();
    }
  }, [user]);

  useEffect(() => {
    if (
      !checkIsOpenOrInQueue() &&
      isLimitedTimeOfferEnabled &&
      isAuthenticated &&
      user &&
      !user?.is_premium &&
      !MirrorService.isMidSite
    ) {
      handleStartTimer();
    }

    return () => {
      handleStopTimer();
    };
  }, [
    user,
    isUserBuyer,
    isAuthenticated,
    isLimitedTimeOfferEnabled,
    checkIsOpenOrInQueue,
    handleStartTimer,
    handleRemoveFromQueue,
    handleStopTimer,
  ]);
};
